import _isEmpty from 'lodash/isEmpty';
import { getRecommendedToeKicks } from '../api';
import { getProductParentIdEntry } from '../store/cabinetVisualizer/actions/utils';
import { useDrawerStore } from '../store/ui';

/**
 * Get color from the design's collection using collectionId
 * @param {Array} baseCabinets - Base cabinet products (used for validation)
 * @param {string} collectionId - Design's collection ID to lookup collection color
 * @returns {string|null} Color name or null if not found
 */
export const getCollectionColor = (baseCabinets, collectionId) => {
  if (!baseCabinets || baseCabinets.length === 0) {
    return null;
  }

  if (!collectionId) {
    return null;
  }

  try {
    // Get the collection from drawerStore using the design's collectionId
    const drawerState = useDrawerStore.getState();

    // First try: Check if the selected collection matches (compare as strings)
    if (drawerState.selectedCollection
        && String(drawerState.selectedCollection.itemId) === String(collectionId)) {
      const collection = drawerState.selectedCollection;

      if (collection?.colorFinish) {
        return collection.colorFinish;
      }

      if (collection?.color) {
        return collection.color;
      }
    }

    // Second try: Search all collections in drawerStore.collections
    if (drawerState.collections?.products) {
      const matchingCollection = drawerState.collections.products.find(
        (coll) => String(coll.itemId) === String(collectionId)
      );

      if (matchingCollection) {
        if (matchingCollection.colorFinish) {
          return matchingCollection.colorFinish;
        }

        if (matchingCollection.color) {
          return matchingCollection.color;
        }
      }
    }

  } catch (err) {
    return null;
  }

  return null;
};

/**
 * Get toekick item ID based on color preference
 * @param {string} colorLastAdded - Color of the last added cabinet
 * @param {Object} toekickMetadata - Metadata containing toekick variations
 * @param {string} previousId - Previous toekick ID (if any)
 * @returns {string} Toekick item ID
 */
export const getToekickItemIdForColor = (colorLastAdded, toekickMetadata, previousId) => {
  const { childItemsLookup, defaultId } = toekickMetadata;

  if (!colorLastAdded) {
    return previousId ?? defaultId;
  }

  const matchedToekick = childItemsLookup?.find((item) => (
    item.attributeCombination?.includes(`${colorLastAdded}~~`)
  ));

  const toekickId = matchedToekick?.itemId ?? previousId ?? defaultId;

  return toekickId;
};

/**
 * Extract base cabinets from products array
 * @param {Array} products - All products in the design
 * @param {Function} getProductType - Function to determine product type by SKU
 * @returns {Array} Filtered array of base cabinets
 */
export const extractBaseCabinets = (products, getProductType) => {
  const baseCabinets = products?.filter((product) => {
    // Exclude spacers (negative SKUs)
    if (product.Sku < 0) {
      return false;
    }

    // Get product type from metadata (ProductParentIds should be populated by now)
    const productType = getProductType(product.Sku) || product.productType;

    return productType === 'Base';
  }) || [];

  return baseCabinets;
};

/**
 * Remove existing toekicks from cart
 * @param {Array} cart - Current cart items
 * @param {Object} productParentIds - ProductParentIds mapping
 * @param {number} toeKickSuperSkuId - Super SKU ID for toekicks
 * @param {Object} toeKickMetadata - Metadata containing toekick child items
 * @returns {Object} { savedCart, prevToeKick }
 */
export const removeExistingToekicks = (cart, productParentIds, toeKickSuperSkuId, toeKickMetadata) => {
  // Get all possible toekick SKUs from metadata
  const toekickSkus = new Set(
    (toeKickMetadata?.childItemsLookup || []).map((item) => String(item.itemId))
  );
  // Also add the default ID if it exists
  if (toeKickMetadata?.defaultId) {
    toekickSkus.add(String(toeKickMetadata.defaultId));
  }

  const savedCart = cart?.filter((item) => {
    const itemSku = String(item.Sku);
    const numericSku = Number(item.Sku);

    // Always keep spacers (SKU -1, -2, -3)
    if (numericSku < 0) {
      return true;
    }

    // Remove if ProductParentId matches toekick super SKU
    const hasToekickParent = Number(productParentIds[itemSku]) === toeKickSuperSkuId;
    // Remove if SKU is in the toekick metadata child items
    const isToekickSku = toekickSkus.has(itemSku);
    return !hasToekickParent && !isToekickSku;
  }) || [];

  const prevToeKick = cart?.find((item) => {
    const itemSku = String(item.Sku);
    const numericSku = Number(item.Sku);

    // Spacers are never toekicks
    if (numericSku < 0) {
      return false;
    }

    const hasToekickParent = Number(productParentIds[itemSku]) === toeKickSuperSkuId;
    const isToekickSku = toekickSkus.has(itemSku);
    return hasToekickParent || isToekickSku;
  });

  return { savedCart, prevToeKick };
};

/**
 * Determine if toekicks should be recalculated
 * @param {Array} baseCabinets - Array of base cabinets
 * @param {Object} addedProductInfo - Info about product being added/removed
 * @returns {boolean} True if toekicks should be recalculated
 */
export const shouldRecalculate = (baseCabinets, addedProductInfo) => {
  if (_isEmpty(baseCabinets)) return false;

  const baseCabinetAdded = addedProductInfo?.newProductType === 'Base'
    && (addedProductInfo?.itemId > 0 || addedProductInfo?.Sku > 0);
  const cabinetDeleted = _isEmpty(addedProductInfo);

  // Always recalculate if we have base cabinets and:
  // - A base cabinet was added
  // - A cabinet was deleted (empty addedProductInfo)
  // - We're doing a fresh calculation (empty addedProductInfo, like from templates)
  return baseCabinetAdded || cabinetDeleted;
};

/**
 * Build toekick cart entry
 * @param {string} toeKickItemId - Toekick item ID
 * @param {number} toeKickCount - Quantity of toekicks
 * @returns {Object} Cart entry object
 */
export const buildToekickCartEntry = (toeKickItemId, toeKickCount) => ({
  Sku: toeKickItemId,
  Quantity: toeKickCount
});

/**
 * Main function: Update cart with recalculated toekicks for a design
 * @param {Object} params - Calculation parameters
 * @param {Array} params.products - All products in design
 * @param {Array} params.cart - Current cart items
 * @param {Object} params.productParentIds - Existing ProductParentIds mapping
 * @param {number} params.toeKickSuperSkuId - Super SKU ID for toekicks
 * @param {Object} params.toeKickMetadata - Metadata for toekick variations
 * @param {Object} params.addedProductInfo - Info about product being added/removed (optional)
 * @param {Function} params.getProductType - Function to determine product type
 * @param {string} params.host - API host URL
 * @param {string} params.collectionId - Design's collection ID for color lookup
 * @returns {Promise<Object>} { cart, productParentIds }
 */
export const updateCartWithToekicks = async ({
  products,
  cart,
  productParentIds,
  toeKickSuperSkuId,
  toeKickMetadata,
  addedProductInfo = {},
  getProductType,
  host,
  collectionId
}) => {
  // Extract base cabinets
  const baseCabinets = extractBaseCabinets(products, getProductType);

  // Remove existing toekicks from cart (using metadata to identify all possible toekick SKUs)
  const { savedCart, prevToeKick } = removeExistingToekicks(
    cart,
    productParentIds,
    toeKickSuperSkuId,
    toeKickMetadata
  );

  // Determine if we should calculate
  const shouldCalculate = shouldRecalculate(baseCabinets, addedProductInfo);

  let updatedParentIds = {};

  // Calculate toekicks if needed
  if (shouldCalculate) {
    const prevToeKickId = prevToeKick?.Sku;

    // Determine color: prefer addedProductInfo.color if it exists, otherwise get from collection
    const cabinetColor = addedProductInfo?.color
      ? addedProductInfo.color
      : getCollectionColor(baseCabinets, collectionId);

    const toeKickItemId = getToekickItemIdForColor(
      cabinetColor,
      toeKickMetadata,
      prevToeKickId
    );

    // API expects the child SKU (color-specific itemId), not the parent SuperSKU
    const toeKickCount = await getRecommendedToeKicks(host, baseCabinets, toeKickItemId);

    if (toeKickCount > 0) {
      updatedParentIds = {
        ...getProductParentIdEntry(toeKickItemId, toeKickSuperSkuId)
      };

      savedCart.push(buildToekickCartEntry(toeKickItemId, toeKickCount));
    }
  } else if (prevToeKick && baseCabinets.length > 0) {
    // Preserve existing toekick if we still have base cabinets
    savedCart.push(prevToeKick);
  }

  return {
    cart: savedCart,
    productParentIds: updatedParentIds
  };
};
